import { useState, useEffect, useRef } from "react";
import { highlightText } from "@speed-highlight/core";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { CopyJsonButton } from "@/components/ui/json-block";
import { getJobStatus } from "@/services/jobsApi";
import type { JobResponse } from "@/services/jobsApi";
import { API_ENDPOINTS } from "@/config/api";

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "in_progress"
      ? "default"
      : status === "queued"
        ? "secondary"
        : status === "complete"
          ? "outline"
          : status === "failed"
            ? "destructive"
            : "outline";
  const label =
    status === "in_progress"
      ? "In progress"
      : status === "not_found"
        ? "Not found"
        : status;
  return (
    <Badge variant={variant} className={cn("uppercase text-xs")}>
      {label}
    </Badge>
  );
}

export interface JobResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string | undefined | null;
}

export function JobResultModal({
  open,
  onOpenChange,
  jobId,
}: JobResultModalProps) {
  const [resultData, setResultData] = useState<JobResponse | null>(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState<string | null>(null);
  const [highlightedResultHtml, setHighlightedResultHtml] = useState<
    string | null
  >(null);
  const highlightJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !jobId) return;
    setResultLoading(true);
    setResultError(null);
    setResultData(null);
    setHighlightedResultHtml(null);
    getJobStatus(jobId)
      .then(setResultData)
      .catch((e) => setResultError(e instanceof Error ? e.message : String(e)))
      .finally(() => setResultLoading(false));
  }, [open, jobId]);

  useEffect(() => {
    if (
      !resultData ||
      resultData.status !== "complete" ||
      resultData.result === undefined ||
      !jobId
    ) {
      setHighlightedResultHtml(null);
      return;
    }
    const rawText =
      typeof resultData.result === "string"
        ? resultData.result
        : JSON.stringify(resultData.result, null, 2);
    highlightJobIdRef.current = jobId;
    setHighlightedResultHtml(null);
    highlightText(rawText, "json", false)
      .then((html) => {
        if (highlightJobIdRef.current === jobId) {
          setHighlightedResultHtml(html);
        }
      })
      .catch(() => {
        if (highlightJobIdRef.current === jobId) {
          setHighlightedResultHtml(null);
        }
      });
    return () => {
      highlightJobIdRef.current = null;
    };
  }, [resultData, jobId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[80vw] h-[80vh] max-w-[80vw] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">
            Job result
            {jobId ? ` · ${jobId}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col gap-3 rounded-md border bg-muted/30 p-4 overflow-hidden">
          {resultLoading && (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading result…
            </div>
          )}
          {resultError && (
            <p className="text-destructive text-sm">{resultError}</p>
          )}
          {!resultLoading && !resultError && resultData && (
            <>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={resultData.status} />
                {resultData.status === "complete" &&
                  resultData.result !== undefined && (
                    <CopyJsonButton
                      value={resultData.result}
                      className="ml-auto"
                    />
                  )}
                {resultData.has_binary_result && (
                  <a
                    href={API_ENDPOINTS.job(resultData.job_id) + "/result"}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    Open binary result →
                  </a>
                )}
              </div>
              {resultData.status === "failed" && resultData.error && (
                <p className="text-destructive text-sm shrink-0">
                  {resultData.error}
                </p>
              )}
              {resultData.status === "complete" &&
                resultData.result !== undefined && (
                  <div className="flex-1 min-h-0 overflow-auto rounded border bg-background p-3 text-xs font-mono whitespace-pre-wrap wrap-break-word">
                    {highlightedResultHtml !== null ? (
                      <div
                        className="shj-lang-json bg-background! text-foreground! p-0! m-0! shadow-none! min-w-0!"
                        dangerouslySetInnerHTML={{
                          __html: highlightedResultHtml,
                        }}
                      />
                    ) : (
                      <pre className="m-0 p-0 bg-transparent">
                        {typeof resultData.result === "string"
                          ? resultData.result
                          : JSON.stringify(resultData.result, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              {resultData.status === "complete" &&
                resultData.result === undefined &&
                !resultData.has_binary_result && (
                  <p className="text-muted-foreground text-sm shrink-0">
                    No result payload.
                  </p>
                )}
              {(resultData.status === "queued" ||
                resultData.status === "in_progress") && (
                <p className="text-muted-foreground text-sm shrink-0">
                  Job has no result yet (status: {resultData.status}).
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
